import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsElement from '../../core/ids-element';

import { removeNewLines, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import './ids-toolbar-section';
import '../ids-menu-button/ids-menu-button';
import type IdsPopupMenu from '../ids-popup-menu/ids-popup-menu';

import styles from './ids-toolbar-more-actions.scss';
import type IdsMenuGroup from '../ids-menu/ids-menu-group';
import type IdsMenuButton from '../ids-menu-button/ids-menu-button';
import type IdsMenuItem from '../ids-menu/ids-menu-item';
import type IdsToolbarSection from './ids-toolbar-section';
import type IdsToolbar from './ids-toolbar';
import IdsButton from '../ids-button/ids-button';

const MORE_ACTIONS_SELECTOR = `[${attributes.MORE_ACTIONS}]`;

const TOOLBAR_TYPES = ['formatter'];

/** Pixel tolerance for overflow detection to account for sub-pixel rounding in getBoundingClientRect */
const OVERFLOW_TOLERANCE_PX = 5;

interface OverflowMenuItem extends HTMLElement {
  overflowTarget?: HTMLElement;
  pinnable?: boolean;
  pinned?: boolean;
  submenu?: IdsPopupMenu;
  text?: string;
  disabled?: boolean;
  container?: HTMLElement;
  selected?: boolean;
}

const Base = IdsColorVariantMixin(
  IdsLocaleMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Toolbar Section Component
 */
@customElement('ids-toolbar-more-actions')
@scss(styles)
export default class IdsToolbarMoreActions extends Base {
  constructor() {
    super();
  }

  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.OVERFLOW,
      attributes.TOOLBAR_TYPE,
      attributes.VISIBLE,
    ];
  }

  /**
   * MutationObserver to watch for disabled attribute changes on overflow menu items
   */
  #menuItemObserver?: MutationObserver | null;

  /**
   * MutationObserver to watch for new child elements being added
   * (handles frameworks like ExtJS that add children after connectedCallback)
   */
  #childObserver?: MutationObserver | null;

  colorVariants = ['alternate-formatter'];

  connectedCallback(): void {
    super.connectedCallback();

    if (!this.menu || !this.button) {
      this.#renderActionsComponents();
    }
    this.#refresh();
    this.#attachEventHandlers();
    this.#setupMenuItemObserver();
    this.#setupChildObserver();
  }

  /**
   * Lifecycle callback that runs after the component is fully mounted.
   * Used for operations that require sibling/parent DOM to be ready,
   * which is especially important for frameworks like ExtJS that add
   * children asynchronously.
   */
  mountedCallback(): void {
    this.#connectOverflowedItems();
    this.#moveOrphanedMenuGroups();

    // Additional delayed check for frameworks like ExtJS that may add children
    // asynchronously after the initial render cycle
    setTimeout(() => {
      this.#moveOrphanedMenuGroups();
    }, 0);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();

    // Cleanup overflow markings on Toolbar items
    // (possibly still present)
    this.overflowMenuItems.forEach((item) => {
      item.removeAttribute(attributes.OVERFLOWED);
    });

    // Cleanup mutation observers
    if (this.#menuItemObserver) {
      this.#menuItemObserver.disconnect();
      this.#menuItemObserver = null;
    }
    if (this.#childObserver) {
      this.#childObserver.disconnect();
      this.#childObserver = null;
    }
  }

  template(): string {
    return `<div class="ids-toolbar-section ids-toolbar-more-actions more">
      <slot></slot>
    </div>`;
  }

  #renderActionsComponents() {
    const menuPopupClass = 'more-actions-menu-popup';
    const menuButtonClass = 'more-actions-button';
    const disabled = this.disabled ? ' disabled' : '';
    const icon = this.getAttribute('icon') || 'more';

    this.insertAdjacentHTML('afterbegin', `<ids-menu-button tooltip="${this.localeAPI.translate('More')}" class="${menuButtonClass}" menu="${menuPopupClass}" ${disabled}>
        <ids-icon icon="${icon}"></ids-icon>
        <span class="audible">More Actions Button</span>
      </ids-menu-button>
      <ids-popup-menu class="${menuPopupClass}" target=".${menuButtonClass}" trigger-type="click"></ids-popup-menu>`);

    // Previously, it was possible to define IdsMenuGroups as child elements
    // of the IdsToolbarMoreActions component. For compatability, these groups
    // are moved into the new menu.
    // Use querySelectorAll without :scope to handle cases where frameworks
    // (like ExtJS) wrap elements in divs.
    const groups = this.querySelectorAll<IdsMenuGroup>('ids-menu-group');
    groups?.forEach((groupEl) => {
      // Only move groups that aren't already inside the popup menu
      if (!groupEl.closest('ids-popup-menu')) {
        this.menu?.append(groupEl);
      }
    });

    // Clean up any empty wrapper divs left behind by frameworks like ExtJS
    // These divs would be in the slot and display incorrectly
    const slotContent = this.container?.querySelector('slot');
    if (slotContent) {
      const emptyWrappers = this.querySelectorAll(':scope > div:not(.ids-toolbar-section):not(.ids-toolbar-more-actions)');
      emptyWrappers.forEach((wrapper) => {
        // Only remove if it's empty or only contains other empty wrappers
        if (!wrapper.querySelector('ids-menu-group, ids-menu-item, ids-popup-menu')) {
          wrapper.remove();
        }
      });
    }
  }

  /**
   * @private
   * @returns {string} the template for the More Actions Menu Group
   */
  #moreActionsMenuTemplate(): string {
    // Cycle through toolbar items, if present, and render a menu item that represents them
    const renderToolbarItems = () => this.toolbar?.items?.map((i: HTMLElement) => this.#moreActionsItemTemplate(i)).join('') || '';
    return `<ids-menu-group ${attributes.MORE_ACTIONS}>
      ${renderToolbarItems()}
    </ids-menu-group>`;
  }

  /**
   * @private
   * @param {HTMLElement} item an element from inside one of the Toolbar sections
   *  that will be mirrored in the More Actions menu
   * @param {boolean} isSubmenuItem true if the item provided is a submenu item
   * @returns {string} representing a single menu item
   */
  #moreActionsItemTemplate(item: HTMLElement, isSubmenuItem = false): string {
    let text: any = '';
    let icon = '';
    let hidden = '';
    let disabled = '';
    let selected = '';
    let submenu = '';
    let overflowed = '';
    let viewbox = '';
    let pinnable = '';
    let itemId = '';

    if (!isSubmenuItem) {
      overflowed = this.isOverflowed(item) ? '' : ' hidden';

      if (item.parentElement?.hasAttribute(attributes.PINNABLE)) {
        pinnable = ' pinnable';
        overflowed = '';
      }
    }

    // NOTE: the `hidden` property is not identified on buttons by design,
    // since the "hidden" attribute is appended to the Toolbar Items
    // to control their visibility when overflowed.
    const handleButton = (thisItem: any) => {
      if (thisItem.id) itemId = ` id="${thisItem.id}-${isSubmenuItem ? 'submenu-item' : 'menu-item'}"`;
      if (thisItem.disabled) disabled = ' disabled';
      if (thisItem.icon) {
        icon = ` icon="${thisItem.icon}"`;
        viewbox = thisItem.iconEl?.viewbox;
        viewbox = viewbox ? ` viewbox="${viewbox}"` : '';
      }
      text = thisItem.text;
    };

    // Top-level Menus/Submenus are handled by this same method
    const handleSubmenu = (thisItem: any) => {
      const menuProp = isSubmenuItem ? 'submenu' : 'menuEl';

      if (thisItem[menuProp]) {
        const thisSubItems = thisItem[menuProp].items;
        const menuGroup = thisItem[menuProp].querySelector('ids-menu-group');
        const selectAttr = menuGroup?.getAttribute(attributes.SELECT);
        const selectStr = selectAttr ? ` select="${selectAttr}"` : '';
        submenu = `<ids-popup-menu slot="submenu">
          <ids-menu-group${selectStr}>
            ${thisSubItems.map((subItem: any) => this.#moreActionsItemTemplate(subItem, true)).join('') || ''}
          </ids-menu-group>
        </ids-popup-menu>`;
      }
    };

    // These represent menu items in top-level menu buttons, which can be hidden.
    const handleMenuItem = (thisItem: any) => {
      if (thisItem.id) itemId = ` id="${thisItem.id}-${isSubmenuItem ? 'submenu-item' : 'menu-item'}"`;
      if (thisItem.disabled) disabled = ' disabled';
      if (thisItem.icon) icon = ` icon="${thisItem.icon}"`;
      if (thisItem.hidden) hidden = ` hidden`;
      if (thisItem.selected) selected = ' selected';
      text = thisItem.text;

      if (thisItem.submenu) {
        handleSubmenu(thisItem);
      }
    };

    const handleTabsContext = (thisItem: any) => {
      const tabsElement = thisItem.querySelector('ids-tabs');
      if (tabsElement) {
        const tabs = tabsElement.querySelectorAll('ids-tab');
        if (tabs.length > 0) {
          text = 'Tabs';
          icon = ' icon="tabs"';
          submenu = `<ids-popup-menu slot="submenu">
            <ids-menu-group>
              ${Array.from(tabs).map((tab: any) => `<ids-menu-item>${tab.textContent?.trim()}</ids-menu-item>`).join('')}
            </ids-menu-group>
          </ids-popup-menu>`;
        }
      }
    };

    switch (item.tagName) {
      case 'IDS-MENU-BUTTON':
        handleButton(item);
        handleSubmenu(item);
        break;
      case 'IDS-MENU-ITEM':
        handleMenuItem(item);
        break;
      case 'IDS-BUTTON':
        handleButton(item);
        break;
      case 'IDS-TABS-CONTEXT':
        handleTabsContext(item);
        break;
      default:
        text = item.textContent;
        break;
    }

    // Sanitize text from Toolbar elements to fit menu items
    text = removeNewLines(text)?.trim();

    const widthAttrs = isSubmenuItem ? '' : ' max-width="300px"';

    return `<ids-menu-item${itemId}${disabled}${selected}${icon}${viewbox}${hidden || overflowed}${pinnable}${widthAttrs}>
      ${text}
      ${submenu}
    </ids-menu-item>`;
  }

  /**
   * @readonly
   * @returns {IdsMenuButton | null} the inner menu button
   */
  get button(): IdsMenuButton | null {
    return this.querySelector<IdsMenuButton>('ids-menu-button') ?? null;
  }

  /**
   * @readonly
   * @returns {IdsPopupMenu | null} the inner popup menu
   */
  get menu(): IdsPopupMenu | null {
    // Use class selector to find our specific popup menu, not submenus
    return this.querySelector<IdsPopupMenu>('ids-popup-menu.more-actions-menu-popup')
      || this.querySelector<IdsPopupMenu>(':scope > ids-popup-menu')
      || null;
  }

  /**
   * @readonly
   * @returns {Array<IdsMenuItem>} get the menu-items inside this IdsToolbarMoreActions
   */
  get menuItems(): Array<IdsMenuItem> {
    return [...this.querySelectorAll<IdsMenuItem>('ids-menu-item')];
  }

  /**
   * @readonly
   * @returns {Array<IdsMenuItem>} list of manually-defined menu items
   */
  get predefinedMenuItems(): Array<IdsMenuItem> {
    return [...this.querySelectorAll<IdsMenuItem>(`:scope > ids-popup-menu > ids-menu-group:not(${MORE_ACTIONS_SELECTOR}) > ids-menu-item`)];
  }

  /**
   * @readonly
   * @returns {Array<OverflowMenuItem>} list of menu items that mirror Toolbar items
   */
  get overflowMenuItems(): Array<OverflowMenuItem> {
    const moreActionsMenu = this.querySelector(MORE_ACTIONS_SELECTOR);
    if (moreActionsMenu) {
      return [...this.querySelector(MORE_ACTIONS_SELECTOR)?.children ?? []] as Array<OverflowMenuItem>;
    }
    return [];
  }

  /**
   * @readonly
   * @returns {any} a reference to this section's toolbar parent node
   */
  get toolbar(): any {
    return this.parentElement;
  }

  get pinnableSection(): IdsToolbarSection | undefined {
    const toolbar = this.toolbar as IdsToolbar;
    return toolbar.sections.find((section): section is IdsToolbarSection => section.tagName === 'IDS-TOOLBAR-SECTION' && (section as IdsToolbarSection).pinnable);
  }

  /**
   * @param {boolean} val true if the More Actions menu should be disabled
   */
  set disabled(val: boolean) {
    const trueVal = stringToBool(val);
    this.toggleAttribute(attributes.DISABLED, trueVal);

    if (this.button) {
      this.button.disabled = trueVal;
    }
    this.container?.classList.toggle(attributes.DISABLED, trueVal);
  }

  /**
   * @returns {boolean} true if the More Actions menu is currently disabled
   */
  get disabled(): boolean {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * @param {boolean | string} val truthy if this More Actions menu should display overflowed items from the toolbar
   */
  set overflow(val: boolean | string) {
    this.toggleAttribute(attributes.OVERFLOW, stringToBool(val));
  }

  /**
   * @returns {boolean} true if this More Actions menu will display overflowed items from the toolbar
   */
  get overflow(): boolean {
    return this.hasAttribute(attributes.OVERFLOW);
  }

  /**
   * @param {string} value the type of toolbar
   */
  set toolbarType(value: string | null) {
    if (value && TOOLBAR_TYPES.includes(value)) {
      this.setAttribute(attributes.TOOLBAR_TYPE, value);
      this.container?.classList.add(value);
    } else {
      this.removeAttribute(attributes.TOOLBAR_TYPE);
      this.container?.classList.remove(TOOLBAR_TYPES[0]);
    }
  }

  /**
   * @returns {string} the type of toolbar
   */
  get toolbarType(): string | null {
    return this.getAttribute(attributes.TOOLBAR_TYPE);
  }

  /**
   * Overrides the standard toolbar section "type" setter, which is always "more" in this case.
   * @param {string} val the type value
   */
  set type(val: string) {
    this.removeAttribute(attributes.TYPE);
  }

  /**
   * Overrides the standard toolbar section "type" getter, which always returns "more" in this case.
   * @returns {string} representing the Toolbar Section type
   */
  get type(): string {
    return 'more';
  }

  /**
   * @param {boolean | string} val alters whether the More Actions menu is displayed/hidden
   */
  set visible(val: boolean | string) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.VISIBLE, '');
      this.menu?.showIfAble();
    } else {
      this.removeAttribute(attributes.VISIBLE);
      this.menu?.hide();
    }
  }

  /**
   * @returns {boolean} true if the More Actions menu is currently displayed
   */
  get visible(): boolean {
    return this.hasAttribute(attributes.VISIBLE) || this.menu?.visible || false;
  }

  /**
   * @returns {void}
   */
  #attachEventHandlers(): void {
    this.onEvent('beforeshow', this.menu, (e: any) => {
      // Skip refresh for bubbled submenu beforeshow events
      const isSubmenuEvent = e.target !== this.menu;
      if (!isSubmenuEvent) {
        this.refreshOverflowedItems();
        this.#syncSubmenuSelectionState();
      }

      // Reflect this event to the host element
      this.triggerEvent('beforeshow', this, {
        bubbles: e.bubbles,
        detail: e.detail
      });

      this.button?.configureMenu?.();
    });

    // Forward `selected` events from the More Actions Menu to their
    // Toolbar Item counterparts.  This is needed for firing Toolbar Actions that
    // are only visible inside the overflow menu.
    this.onEvent('selected', this.menu, (e: any) => {
      const menuItem = e.detail.elem;
      if (menuItem.overflowTarget) {
        e.preventDefault();
        e.stopPropagation();

        // Sync selection state back to the original menu item
        const originalItem = menuItem.overflowTarget;
        if (originalItem.select || originalItem.group?.select) {
          originalItem.selected = true;
        }

        this.toolbar.triggerSelectedEvent(menuItem, true);
      }
    });

    // Listen to show/hide events from the inner IdsPopupMenu and reflect the `visible` attribute
    this.onEvent('show', this.menu, () => {
      this.visible = !!this.menuItems?.length;
    });
    this.onEvent('hide', this.menu, () => {
      this.visible = false;
    });

    this.onEvent('pinned', this.menu, (e) => {
      const toolbarButton = e.target.overflowTarget as Element;
      this.#pinButton(toolbarButton, e.detail.pinned && !e.target.hidden);
    });

    // Listen for disabled state changes on overflow menu items
    this.onEvent('change', this.menu, (e: any) => {
      const menuItem = e.target;
      if (menuItem.overflowTarget && e.detail?.attribute === 'disabled') {
        if (this.toolbar?.disabled) return;
        menuItem.overflowTarget.disabled = stringToBool(menuItem.disabled);
      }
    });
  }

  /**
   * Sets up MutationObserver to watch for disabled attribute changes on overflow menu items
   * @private
   */
  #setupMenuItemObserver(): void {
    if (!this.menu) return;

    this.#menuItemObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          const menuItem = mutation.target as IdsMenuItem & { overflowTarget?: IdsButton };
          if (menuItem.overflowTarget && !this.toolbar?.disabled) {
            menuItem.overflowTarget.disabled = stringToBool(menuItem.disabled);

            if (menuItem.id?.includes('-submenu-item')) {
              menuItem.setAttribute('data-was-overflowed', '');
            }
          }
        }
      });
    });

    this.#menuItemObserver.observe(this.menu, {
      attributes: true,
      attributeFilter: ['disabled'],
      subtree: true
    });
  }

  /**
   * Sets up MutationObserver to watch for new child elements being added.
   * This handles frameworks like ExtJS that add children after connectedCallback.
   * @private
   */
  #setupChildObserver(): void {
    this.#childObserver = new MutationObserver((mutations) => {
      let needsCleanup = false;

      for (const mutation of mutations) {
        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;

        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          const element = node as Element;

          // Check if this element is an ids-menu-group or contains one
          const menuGroups = element.tagName === 'IDS-MENU-GROUP'
            ? [element]
            : element.querySelectorAll?.('ids-menu-group');

          if (!menuGroups || menuGroups.length === 0) continue;

          for (const groupEl of menuGroups) {
            // Skip if already inside a popup menu
            if (groupEl.closest('ids-popup-menu')) continue;

            // Skip if inside a menu item (submenu's group)
            if (groupEl.closest('ids-menu-item')) continue;

            // Skip if not a descendant of this more-actions element
            if (!this.contains(groupEl)) continue;

            // Skip if the menu group contains our popup menu (circular reference)
            if (this.menu && groupEl.contains(this.menu)) continue;

            // Move the menu group to our popup menu
            if (this.menu) {
              this.menu.append(groupEl);
              needsCleanup = true;
            }
          }
        }
      }

      // Batch cleanup after processing all mutations
      if (needsCleanup) {
        this.#cleanupEmptyWrappers();
      }
    });

    // Only observe direct children - ExtJS adds wrapper divs as direct children
    this.#childObserver.observe(this, {
      childList: true,
      subtree: false
    });
  }

  /**
   * Removes empty wrapper divs that may be left behind after moving menu groups
   * @private
   */
  #cleanupEmptyWrappers(): void {
    // Find all direct child divs that are empty or only contain other empty divs
    const wrappers = this.querySelectorAll(':scope > div:not(.ids-toolbar-section):not(.ids-toolbar-more-actions)');
    wrappers.forEach((wrapper) => {
      if (!wrapper.querySelector('ids-menu-group, ids-menu-item, ids-popup-menu, ids-menu-button')) {
        wrapper.remove();
      }
    });
  }

  /**
   * Moves any orphaned menu groups (direct children not in popup) to the popup menu.
   * This handles cases where frameworks like ExtJS add children synchronously
   * before the MutationObserver is set up.
   * @private
   */
  #moveOrphanedMenuGroups(): void {
    if (!this.menu) return;

    // Find all direct child menu groups that aren't in the popup menu
    const orphanedGroups = this.querySelectorAll(':scope > ids-menu-group');
    orphanedGroups.forEach((groupEl) => {
      // Double-check it's not already in a popup menu
      if (!groupEl.closest('ids-popup-menu')) {
        // Skip if this group contains our popup menu (would cause circular reference)
        if (groupEl.contains(this.menu!)) return;
        this.menu!.append(groupEl);
      }
    });

    // Also check for menu groups inside wrapper divs
    const wrapperGroups = this.querySelectorAll(':scope > div > ids-menu-group');
    wrapperGroups.forEach((groupEl) => {
      if (!groupEl.closest('ids-popup-menu')) {
        // Skip if this group contains our popup menu (would cause circular reference)
        if (groupEl.contains(this.menu!)) return;
        this.menu!.append(groupEl);
      }
    });

    // Clean up empty wrappers
    this.#cleanupEmptyWrappers();
  }

  /**
   * Refreshes the state of the More Actions button
   * @returns {void}
   */
  #refresh(): void {
    if (this.menu?.popup) {
      this.menu.popup.align = 'bottom, right';
      this.menu.popup.alignEdge = 'bottom';
    }
    this.onColorVariantRefresh();
  }

  /**
   * Refreshes the visible state of menu items representing "overflowed" elements
   * @returns {void}
   */
  refreshOverflowedItems(): void {
    if (!this.overflow) return;

    // Use custom overflow handling for non-formatter toolbars
    const isFormatterToolbar = this.toolbarType === 'formatter';
    if (!isFormatterToolbar) {
      this.#handleAllSectionsOverflow();
    }

    const allOverflowItems = this.menu?.querySelectorAll('ids-menu-item') || [];

    [...allOverflowItems].forEach((element) => {
      const item = element as IdsMenuItem & { overflowTarget?: IdsButton };
      const toolbarButton = item.overflowTarget;

      if (!toolbarButton) return;

      // Skip synchronization if the toolbar itself is disabled
      if (this.toolbar?.disabled) return;

      const wasOverflowed = item.hasAttribute('data-was-overflowed');
      const isNowOverflowed = toolbarButton?.hasAttribute(attributes.OVERFLOWED)
        || (toolbarButton && this.isOverflowed(toolbarButton));

      const itemText = item.text?.trim() ?? '';
      const toolbarBtnText = toolbarButton.text?.trim() ?? '';

      if (toolbarBtnText.length > 0 && itemText !== toolbarBtnText) {
        item.text = toolbarBtnText;
      }

      if (toolbarButton.disabled !== undefined) {
        if (isNowOverflowed) {
          item.disabled = stringToBool(toolbarButton.disabled);
          if (item.disabled) {
            item.setAttribute('data-was-overflowed', '');
            item.container?.classList.add('disabled');
          }
        } else if (wasOverflowed) {
          toolbarButton.disabled = stringToBool(item.disabled);
          item.removeAttribute('data-was-overflowed');
        } else {
          item.disabled = stringToBool(toolbarButton.disabled);
          if (item.disabled) {
            item.container?.classList.add('disabled');
          } else {
            item.container?.classList.remove('disabled');
          }
        }
      }
    });

    this.overflowMenuItems.forEach((item, index) => {
      const toolbarButton = item.overflowTarget as Element;
      const isAppMenu = index === 0 && item.getAttribute(attributes.ICON) === 'menu';
      if (item.pinnable) {
        item.hidden = false;
        this.#pinButton(toolbarButton, item.pinned ?? false);
      } else if (!isAppMenu) {
        if (isFormatterToolbar) {
          const doHide = !this.isOverflowed(toolbarButton);
          const pinnableItems = this.pinnableSection?.items || [];
          const hasOverflowPins = pinnableItems.some((pinItem) => this.isOverflowed(pinItem));
          item.hidden = doHide && !hasOverflowPins;
        } else {
          const isOverflowed = toolbarButton?.hasAttribute(attributes.OVERFLOWED);
          const isToolbarButtonHidden = (toolbarButton as HTMLElement)?.hidden || false;
          // Hide overflow item if button is not overflowed OR if the toolbar button itself is hidden
          item.hidden = !isOverflowed || isToolbarButtonHidden;
        }
      }

      // Sync beforeShow callbacks for submenu items
      const originalMenu = (toolbarButton as any)?.menuEl;
      if (toolbarButton.getAttribute(attributes.MENU) && originalMenu) {
        this.#syncBeforeShowCallback(item, originalMenu);
      }
    });

    if (this.button) {
      this.button.hidden = !this.hasVisibleActions();
      if (this.button.getAttribute(attributes.COLOR_VARIANT)) this.button.container?.classList.add(`color-variant-${this.button.getAttribute(attributes.COLOR_VARIANT)}`);
    }

    if (this.menu?.visible) {
      this.menu.refreshIconAlignment();
    }
  }

  /**
   * Syncs a specific attribute from a toolbar button to its overflow menu item
   * @param {HTMLElement} toolbarButton the toolbar button whose attribute changed
   * @param {string} attributeName the name of the attribute that changed
   * @param {string | null} newValue the new value of the attribute
   * @returns {void}
   */
  syncAttributeToOverflow(toolbarButton: HTMLElement, attributeName: string, newValue: string | null): void {
    if (!this.overflow) return;

    // Find the overflow menu item that corresponds to this toolbar button
    const overflowItem = this.overflowMenuItems.find((item: any) => item.overflowTarget === toolbarButton);
    if (!overflowItem) return;

    // Sync the attribute to the overflow menu item
    switch (attributeName) {
      case 'icon': {
        // Update icon attribute and viewbox if present
        if (newValue) {
          overflowItem.setAttribute('icon', newValue);

          // Also sync viewbox if it exists on the toolbar button's icon
          const toolbarIcon = (toolbarButton as any).iconEl;
          if (toolbarIcon?.viewbox) {
            overflowItem.setAttribute('viewbox', toolbarIcon.viewbox);
          }
        } else {
          overflowItem.removeAttribute('icon');
          overflowItem.removeAttribute('viewbox');
        }
        break;
      }
      case 'text': {
        // Update the text content of the menu item
        const text = (toolbarButton as any).text || toolbarButton.textContent?.trim() || '';
        overflowItem.text = text;
        break;
      }
      case 'appearance':
      case 'color-variant': {
        // Sync appearance and color-variant attributes directly
        if (newValue) {
          overflowItem.setAttribute(attributeName, newValue);
        } else {
          overflowItem.removeAttribute(attributeName);
        }
        break;
      }
      case 'hidden': {
        // Sync the hidden attribute to the overflow menu item
        if (newValue !== null) {
          overflowItem.setAttribute('hidden', newValue);
        } else if (toolbarButton.hasAttribute('hidden')) {
          overflowItem.setAttribute('hidden', '');
        } else {
          overflowItem.removeAttribute('hidden');
        }
        break;
      }
      default:
        // For any other attributes, sync them directly
        if (newValue !== null) {
          overflowItem.setAttribute(attributeName, newValue);
        } else {
          overflowItem.removeAttribute(attributeName);
        }
    }

    // Refresh icon alignment if the menu is visible and icon changed
    if (attributeName === 'icon' && this.menu?.visible) {
      this.menu.refreshIconAlignment();
    }
  }

  /**
   * Rebuilds the overflow menu contents from scratch
   * @returns {void}
   */
  rebuildOverflowMenu(): void {
    if (!this.overflow || !this.menu) return;

    // Remove existing overflow menu group
    const existingGroup = this.menu.querySelector(MORE_ACTIONS_SELECTOR);
    existingGroup?.remove();

    // Recreate the overflow menu group with current toolbar items
    this.menu.insertAdjacentHTML('afterbegin', this.#moreActionsMenuTemplate());

    // Reconnect all overflow items to their toolbar counterparts
    this.overflowMenuItems.forEach((item: any, i: number) => {
      item.overflowTarget = this.toolbar.items[i];
      if (item.submenu) {
        this.#handleSubmenuConnections(item, item.overflowTarget.menuEl);
      }
    });

    // Refresh the overflow state
    this.refreshOverflowedItems();
  }

  /**
   * Recursively sync disabled state for submenu items
   * @param {any} submenu The submenu element containing items to sync
   * @param {boolean} isOverflowed Whether the submenu is currently overflowed
   * @private
   */
  #syncSubmenuItems(submenu: any, isOverflowed: boolean): void {
    if (!submenu) return;

    [...submenu.children].forEach((element) => {
      const subItem = element as IdsMenuItem & { overflowTarget?: IdsButton };
      if (subItem.overflowTarget && subItem.overflowTarget.disabled !== undefined) {
        const wasOverflowed = subItem.hasAttribute('data-was-overflowed');
        const toolbarItem = subItem.overflowTarget;

        if (isOverflowed) {
          subItem.disabled = stringToBool(toolbarItem.disabled);
          subItem.setAttribute('data-was-overflowed', '');
        } else if (wasOverflowed) {
          toolbarItem.disabled = stringToBool(subItem.disabled);
          subItem.removeAttribute('data-was-overflowed');
        } else {
          subItem.disabled = stringToBool(toolbarItem.disabled);
        }
      }

      if (subItem.submenu) {
        this.#syncSubmenuItems(subItem.submenu, isOverflowed);
      }
    });
  }

  /**
   * Helper to handle submenu connections during rebuild
   * @param {any} menuItem The menu item whose submenu connections are being handled
   * @param {any} targetMenu The target menu whose items are being connected as submenu items
   * @private
   */
  #handleSubmenuConnections(menuItem: any, targetMenu: any): void {
    if (!targetMenu) return;

    // Use .items property to get actual menu items (skips menu-group wrappers)
    const submenuItems = menuItem.submenu?.items || [];
    const targetItems = targetMenu.items || [];

    submenuItems.forEach((item: OverflowMenuItem, i: number) => {
      if (targetItems[i]) {
        item.overflowTarget = targetItems[i];

        // Recursively handle nested submenus
        if (item.submenu && targetItems[i].submenu) {
          this.#handleSubmenuConnections(item, targetItems[i].submenu);
        }
      }
    });
  }

  /**
   * Update the pinned status of a button
   * @param {Element} button button element
   * @param {boolean} pinned pin status. True if should be pinned.
   */
  #pinButton(button: Element, pinned: boolean) {
    if (button) {
      if (pinned) {
        button.removeAttribute(attributes.OVERFLOWED);
        button.setAttribute(attributes.PINNED, '');
        button.classList.remove(attributes.OVERFLOWED);
      } else {
        button.setAttribute(attributes.OVERFLOWED, '');
        button.removeAttribute(attributes.PINNED);
        button.classList.add(attributes.OVERFLOWED);
      }

      const section = button.parentElement as IdsToolbarSection;
      const pinsEmpty = section.pinned.length <= 0;

      if (section) {
        if (pinsEmpty) {
          section.classList.add('pinned-empty');
        } else {
          section.classList.remove('pinned-empty');
          if (section.classList.length === 0) section.removeAttribute('class');
        }

        this.#updateSections(pinsEmpty);
      }
    }
  }

  #updateSections(pinsEmpty: boolean) {
    const sections = (this.toolbar as IdsToolbar).sections;

    if (!sections || sections.length === 0) return;

    if (pinsEmpty) {
      sections.forEach((section) => {
        if (section?.name !== 'ids-toolbar-more-actions' && 'classList' in section) {
          section.classList.remove('has-pins');
        }
      });
    } else {
      sections.forEach((section) => {
        if (section?.name !== 'ids-toolbar-more-actions' && 'classList' in section) {
          section.classList.add('has-pins');
        }
      });
    }
  }

  /**
   * Syncs beforeShow callback from an original menu to its overflow counterpart
   * @private
   * @param {any} item the overflow menu item
   * @param {any} originalMenu the original popup menu element
   */
  #syncBeforeShowCallback(item: any, originalMenu: any): void {
    const popupOverflow = item.querySelector('ids-popup-menu') as IdsPopupMenu;
    if (popupOverflow && originalMenu.beforeShow) {
      popupOverflow.beforeShow = originalMenu.beforeShow;
    }
  }

  /**
   * Syncs selected state from original menu items to their overflow counterparts
   * using the overflowTarget reference on each submenu item
   * @private
   */
  #syncSubmenuSelectionState(): void {
    this.overflowMenuItems?.forEach((item) => {
      if (!item.submenu) return;

      // Only sync selection for menus with a select attribute
      const menuGroup = item.submenu?.groups?.[0];
      if (!menuGroup?.hasAttribute(attributes.SELECT)) return;

      const overflowItems = item.submenu.items || [];

      overflowItems.forEach((overflowItem: OverflowMenuItem) => {
        const originalItem = overflowItem.overflowTarget as IdsMenuItem | undefined;
        if (!originalItem) return;

        const isSelected = originalItem.selected || originalItem.hasAttribute(attributes.SELECTED);
        if (isSelected !== !!overflowItem.selected) {
          overflowItem.selected = isSelected;
        }
      });
    });
  }

  /**
   * Connects each overflowed menu item to a real Toolbar element
   * @private
   * @returns {void}
   */
  #connectOverflowedItems(): void {
    if (!this.menu) return;

    // Render the "More Actions" area if it doesn't exist
    const el = this.menu.querySelector(MORE_ACTIONS_SELECTOR);
    if (!el && this.overflow) {
      this.menu!.insertAdjacentHTML('afterbegin', this.#moreActionsMenuTemplate());
    }
    if (el && !this.overflow) {
      el.remove();
    }

    // Connect all "More Action" items generated from Toolbar Elements to their
    // real counterparts in the Toolbar
    this.overflowMenuItems.forEach((item: any, i: number) => {
      item.overflowTarget = this.toolbar.items[i];

      // Check if button has a popup menu and insert beforeShow for element
      const menu = item.overflowTarget.getAttribute(attributes.MENU);

      if (menu) {
        const popupTarget = item.overflowTarget.menuEl;
        const popupOverflow = item.querySelector('ids-popup-menu') as IdsPopupMenu;

        if (popupOverflow) {
          popupOverflow.isOverflowItem = true;
          popupOverflow.id = `${item.id}-${i}`;
        }

        if (popupOverflow && popupTarget?.beforeShow) {
          popupOverflow.beforeShow = popupTarget?.beforeShow;
        }
      }

      if (item.submenu) {
        this.#handleSubmenuConnections(item, item.overflowTarget.menuEl);
      }
    });
  }

  /**
   * Passes focus from the main element into the inner Ids Menu Button
   * @returns {void}
   */
  focus(): void {
    if (!this.visible) {
      this.button?.focus();
    } else {
      this.menu?.focusTarget?.focus();
    }
  }

  /**
   * @returns {boolean} true if there are currently visible actions in this menu
   */
  hasVisibleActions(): boolean {
    return this.predefinedMenuItems.length > 0
      || this.querySelectorAll(`:scope > ids-popup-menu > ids-menu-group${MORE_ACTIONS_SELECTOR} > ids-menu-item:not([hidden])`).length > 0;
  }

  /**
   * @returns {boolean} true if there are currently enabled (read: not disabled) actions in this menu
   */
  hasEnabledActions(): boolean {
    return this.querySelectorAll(`:scope > ids-popup-menu > ids-menu-group${MORE_ACTIONS_SELECTOR} > ids-menu-item:not([disabled])`).length > 0;
  }

  /**
   * Calculate overflow across all buttonset sections together.
   * Items are overflowed right-to-left (in LTR) across sections so that
   * the rightmost section's items go into the overflow menu first.
   * @private
   */
  #handleAllSectionsOverflow(): void {
    const buttonsetSections: IdsToolbarSection[] = this.toolbar.sections
      .filter((s: IdsToolbarSection) => s.type === 'buttonset');

    if (buttonsetSections.length === 0) return;

    // Sections with `favor` attribute or type="static" never have their items collapsed
    const favoredSections = new Set(
      buttonsetSections.filter((s: IdsToolbarSection) => s.favor || s.type === 'static')
    );

    const allItems: HTMLElement[] = [];
    const itemSectionMap = new Map<HTMLElement, IdsToolbarSection>();
    buttonsetSections.forEach((section: IdsToolbarSection) => {
      const items = section.items.filter((item: HTMLElement) => !item.hidden);
      items.forEach((item) => itemSectionMap.set(item, section));
      allItems.push(...items);
    });

    if (allItems.length === 0) return;

    allItems.forEach((item) => {
      item.removeAttribute(attributes.OVERFLOWED);
      item.classList.remove(attributes.OVERFLOWED);
    });

    // Cache rects — re-measured after each overflow change
    let sectionRects = new Map<IdsToolbarSection, DOMRect>();
    let itemRects = new Map<HTMLElement, DOMRect>();

    const measureRects = () => {
      sectionRects = new Map();
      buttonsetSections.forEach((section) => {
        sectionRects.set(section, section.getBoundingClientRect());
      });
      itemRects = new Map();
      allItems.forEach((item) => {
        if (!item.hasAttribute(attributes.OVERFLOWED)) {
          itemRects.set(item, item.getBoundingClientRect());
        }
      });
    };
    measureRects();

    const isRtl = this.localeAPI.isRTL();
    const anyItemOverflowed = (): boolean => {
      for (const section of buttonsetSections) {
        if (favoredSections.has(section)) continue;
        const sectionRect = sectionRects.get(section)!;
        for (const item of allItems) {
          if (itemSectionMap.get(item) !== section) continue;
          if (item.hasAttribute(attributes.OVERFLOWED)) continue;
          const itemRect = itemRects.get(item);
          if (!itemRect) continue;
          if (itemRect.right > sectionRect.right + OVERFLOW_TOLERANCE_PX
            || itemRect.left < sectionRect.left - OVERFLOW_TOLERANCE_PX) {
            return true;
          }
        }
      }
      return false;
    };

    // Exclude favored sections from overflow processing
    const overflowableItems = allItems.filter((item) => !favoredSections.has(itemSectionMap.get(item)!));
    const sortedItems = [...overflowableItems].map((item, index) => {
      const section = itemSectionMap.get(item);
      const sectionIndex = buttonsetSections.indexOf(section!);
      return {
        item,
        index,
        sectionIndex,
        edge: isRtl
          ? (itemRects.get(item)?.left ?? 0)
          : (itemRects.get(item)?.right ?? 0)
      };
    }).sort((a, b) => {
      // First, sort by section order (to maintain cross-section DOM order)
      if (a.sectionIndex !== b.sectionIndex) {
        return isRtl ? a.sectionIndex - b.sectionIndex : b.sectionIndex - a.sectionIndex;
      }
      // Within the same section, sort by edge position
      const edgeDiff = isRtl ? a.edge - b.edge : b.edge - a.edge;
      if (edgeDiff !== 0) return edgeDiff;
      // If edges are equal, maintain DOM order
      return a.index - b.index;
    }).map((entry) => entry.item);

    let processIndex = 0;
    while (processIndex < sortedItems.length && anyItemOverflowed()) {
      const item = sortedItems[processIndex];
      if (!item.hasAttribute(attributes.OVERFLOWED)) {
        item.setAttribute(attributes.OVERFLOWED, '');
        item.classList.add(attributes.OVERFLOWED);
        // Re-measure all rects after DOM change
        measureRects();
      }
      processIndex++;
    }
  }

  /**
   * @param {HTMLElement} item reference to the toolbar item to be checked for overflow
   * @returns {boolean} true if the item is a toolbar member and should be displayed by overflow
   */
  isOverflowed(item: any): boolean {
    if (!this.toolbar.contains(item)) {
      return false;
    }
    if (item.hidden) {
      return false;
    }

    // Items in favored or static sections are never considered overflowed
    const section = item.parentElement;
    if (section?.hasAttribute?.(attributes.FAVOR)
      || section?.getAttribute?.(attributes.TYPE) === 'static') {
      return false;
    }

    const itemRect = item.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();

    const isBeyondRightEdge = itemRect.right > sectionRect.right;
    const isBeyondLeftEdge = itemRect.left < sectionRect.left;

    return isBeyondLeftEdge || isBeyondRightEdge;
  }

  onColorVariantRefresh(): void {
    const colorVariant = this.colorVariant;
    if (colorVariant === 'alternate-formatter') {
      this.button?.setAttribute(attributes.COLOR_VARIANT, 'alternate-formatter');
    } else {
      this.button?.removeAttribute(attributes.COLOR_VARIANT);
    }
  }
}
